import { cross, dot, sampleNativePlane, savedVolumeSampler } from "@/predictions/resampleSavedCard";

type Point = [number, number, number];

/** Stored-file affine maps (column,row,slice); bytes vary row first.
 * Reuse the card sampler's inverse/order conversion, not another axis swap.
 */
export function validateSavedMaskAffine(affine: number[]) {
  const axes = [0, 1, 2].map((i) => [affine[i], affine[i + 4], affine[i + 8]]);
  if (
    affine.length !== 12 ||
    !affine.every(Number.isFinite) ||
    Math.abs(dot(axes[0], cross(axes[1], axes[2]))) < 1e-10
  ) {
    throw new Error("Saved mask has invalid or singular geometry");
  }
}

/** Only the historical positive-diagonal, zero-origin fingerprint uses legacy loading.
 * A zero origin with oriented axes is valid geometry, not a legacy marker.
 */
export function isLegacyMaskAffine(affine: number[]) {
  return (
    [3, 7, 11, 1, 2, 4, 6, 8, 9].every((i) => Math.abs(affine[i]) < 1e-6) &&
    [0, 5, 10].every((i) => affine[i] > 0)
  );
}

/** A slice axis shorter than 0.01 mm: saved from a stack before ENG-258, whose
 *  vtkImageData gave Cornerstone's placeholder spacing (EPSILON, 0.001). Its
 *  header is wrong; its voxels are right by the stack's list index. */
export function isStackEpsilonAffine(affine: number[]) {
  return Math.hypot(affine[2], affine[6], affine[10]) < 0.01;
}

export function savedMaskTargetPlane(metadata: any) {
  const {
    rows,
    columns,
    rowPixelSpacing: rowSpacing,
    columnPixelSpacing: colSpacing,
  } = metadata ?? {};
  const vector = (value: ArrayLike<number> | undefined) =>
    value?.length === 3 ? Array.from(value) : [];
  const origin = vector(metadata?.imagePositionPatient);
  const x = vector(metadata?.rowCosines),
    y = vector(metadata?.columnCosines);
  if (
    !Number.isSafeInteger(rows) ||
    rows <= 0 ||
    !Number.isSafeInteger(columns) ||
    columns <= 0 ||
    origin.length !== 3 ||
    x.length !== 3 ||
    y.length !== 3 ||
    ![...origin, ...x, ...y, rowSpacing, colSpacing].every(Number.isFinite) ||
    rowSpacing <= 0 ||
    colSpacing <= 0 ||
    Math.abs(dot(x, x) - 1) > 1e-3 ||
    Math.abs(dot(y, y) - 1) > 1e-3 ||
    Math.abs(dot(x, y)) > 1e-3
  ) {
    throw new Error("CT image has missing or invalid plane geometry");
  }
  const normal = cross(x, y);
  const norm = Math.sqrt(dot(normal, normal));
  return {
    rows,
    columns,
    frame: {
      rows,
      columns,
      image_position_patient: origin,
      image_orientation_patient: [...x, ...y],
      pixel_spacing: `${rowSpacing}\\${colSpacing}`,
    },
    pixelToWorld: (column: number, row: number): Point =>
      origin.map((v, i) => v + x[i] * column * colSpacing + y[i] * row * rowSpacing) as Point,
    normal: normal.map((v) => v / norm),
    viewUp: y.map((v) => -v),
  };
}

/** Slice `sliceIndex` of the viewport volume, in the frame Save writes the affine from.
 * Per-image metadata switches from sidecar to DICOM geometry as slices stream;
 * the volume's origin/direction/spacing are fixed when it is built.
 */
export function volumeSlicePlane(imageData: any, sliceIndex: number) {
  const vector = (value: ArrayLike<number> | undefined, length: number) =>
    value?.length === length ? Array.from(value, Number) : [];
  const origin = vector(imageData?.getOrigin?.(), 3);
  const direction = vector(imageData?.getDirection?.(), 9);
  const [colSpacing, rowSpacing, sliceSpacing] = vector(imageData?.getSpacing?.(), 3);
  const [columns, rows] = vector(imageData?.getDimensions?.(), 3);
  const x = direction.slice(0, 3),
    y = direction.slice(3, 6),
    z = direction.slice(6, 9);
  if (
    origin.length !== 3 ||
    direction.length !== 9 ||
    !Number.isFinite(sliceSpacing) ||
    sliceSpacing <= 0 ||
    Math.abs(dot(z, z) - 1) > 1e-3 ||
    Math.abs(dot(z, x)) > 1e-3 ||
    Math.abs(dot(z, y)) > 1e-3
  ) {
    throw new Error("CT volume has missing or invalid geometry");
  }
  return savedMaskTargetPlane({
    rows,
    columns,
    rowPixelSpacing: rowSpacing,
    columnPixelSpacing: colSpacing,
    imagePositionPatient: origin.map((v, i) => v + sliceIndex * sliceSpacing * z[i]),
    rowCosines: x,
    columnCosines: y,
  });
}

/** Build sparse label runs once, then allocate only one destination slice at a time. */
export function createSavedMaskSampler(
  data: Uint16Array,
  shape: { rows: number; columns: number },
  affine: number[],
  includedLabels?: ReadonlySet<number>,
) {
  validateSavedMaskAffine(affine);
  const runs = new Map<number, [number, number][]>();
  for (let start = 0; start < data.length;) {
    const label = data[start];
    let end = start + 1;
    while (end < data.length && data[end] === label) end++;
    if (label && (!includedLabels || includedLabels.has(label))) {
      const labelRuns = runs.get(label) ?? [];
      labelRuns.push([start, end - start]);
      runs.set(label, labelRuns);
    }
    start = end;
  }
  const samplers = [...runs].map(([label, labelRuns]) => {
    const sampler = savedVolumeSampler({ ...shape, affine, runs: labelRuns });
    if (!sampler) throw new Error("Saved mask cannot be sampled");
    return { label, sampler };
  });
  return (plane: ReturnType<typeof savedMaskTargetPlane>) => {
    const labels = new Uint16Array(plane.rows * plane.columns);
    for (const { label, sampler } of samplers) {
      const sampled = sampleNativePlane(sampler, plane.frame);
      if (!sampled) throw new Error("CT image cannot be sampled");
      for (const [start, length] of sampled.runs) labels.fill(label, start, start + length);
    }
    return labels;
  };
}
